import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';

const DEFAULT_DATA = {
  id: '',
  type: 'assn',
  title: '',
  start: '12.5',
  duration: '15',
  environment: '',
  concepts: '',
  exampleId: '',
  slidesLink: '',
  handoutLink: '',
};

// Converts "HH:MM" string to decimal hour (e.g. "13:15" → 13.25)
const timeStringToDecimal = timeStr => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours + minutes / 60;
};

// Converts decimal hour to "HH:MM" string (e.g. 13.5 → "13:30")
const decimalToTimeString = decimal => {
  const hours = Math.floor(decimal);
  const minutes = Math.round((decimal - hours) * 60);
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const EventModal = ({
  initialData,
  mode = 'add',
  onSubmit,
  onClose,
  allAssignments = [],
  cipAssignments = [],
}) => {
  // Keep track of the form data
  const [formData, setFormData] = useState(DEFAULT_DATA);

  // Keep track of the start time in string format
  const [startTimeInput, setStartTimeInput] = useState('12:30');

  // Update the form data based on any information that is passed in
  useEffect(() => {
    if (initialData) {
      // Store the start time in decimal form
      const decimalStart = initialData.start || DEFAULT_DATA.start;

      // Update the state with the initialData
      setFormData({
        ...DEFAULT_DATA,
        ...initialData,
        start: String(decimalStart),
        duration: String(initialData.duration || DEFAULT_DATA.duration), // Make sure the duration is a string
      });

      // Update the start time state
      setStartTimeInput(decimalToTimeString(decimalStart));
    }
  }, [initialData]);

  // Booleans to control what the form shows
  const editMode = mode === 'edit';
  const allAssignmentIds = allAssignments.map(a => a.name);

  const [isAssignment, setIsAssignment] = useState(formData.type === 'assn');
  const [isCip, setIsCip] = useState(formData.environment === 'ide');

  // Determine what assignments to show based on the environment
  const [assignmentsList, setAssignmentsList] = useState([]);
  useEffect(() => {
    if (isCip) {
      setAssignmentsList(cipAssignments);
    } else {
      setAssignmentsList(allAssignmentIds);
    }
  }, [isCip]);

  // Make sure that we update the state variables as the input changes
  useEffect(() => {
    setIsAssignment(formData.type === 'assn');
    setIsCip(formData.environment === 'ide');
  }, [formData]);

  // Update the form data
  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Submit the form
  const handleSubmit = () => {
    // Create an event with all of the form data
    // and the times converted to numbers
    const event = {
      ...formData,
      start: parseFloat(formData.start),
      duration: parseInt(formData.duration),
    };

    // Call the submit function and close the modal
    onSubmit(event);
    onClose();
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{editMode ? 'Edit Event' : 'Add Event'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form>
          <Field>
            <label>Type:</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="assn">Assignment</option>
              <option value="lecture">Lecture</option>
              <option value="break">Lunch</option>
              <option value="handout">Handout</option>
            </select>
          </Field>

          {isAssignment && (
            <>
              <Field>
                <label>Environment:</label>
                <select
                  name="environment"
                  value={formData.environment}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Choose --</option>
                  <option value="ide">cip</option>
                  <option value="github">other</option>
                </select>
              </Field>

              <Field>
                <label>Assignment:</label>
                <select
                  name="id"
                  value={formData.id}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Choose --</option>
                  {assignmentsList.map(a => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </Field>

              <Field>
                <label>Worked Example:</label>
                <select
                  name="exampleId"
                  value={formData.exampleId}
                  onChange={handleChange}
                  required
                >
                  <option value="">-- Choose --</option>
                  {assignmentsList.map(a => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}

          <Field>
            <label>Title:</label>
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </Field>

          {!isAssignment && (
            <Field>
              <label>ID:</label>
              <input
                name="id"
                value={formData.id}
                onChange={handleChange}
                required
              />
            </Field>
          )}

          <Field>
            <label>New Concepts (for loops):</label>
            <input
              required
              value={formData.concepts}
              onChange={e =>
                setFormData({ ...formData, concepts: e.target.value })
              }
            />
          </Field>

          <Field>
            <label>Start Time (HH:MM):</label>
            <input
              type="time"
              value={startTimeInput}
              onChange={e => {
                const timeVal = e.target.value;
                setStartTimeInput(timeVal);
                setFormData(prev => ({
                  ...prev,
                  start: String(timeStringToDecimal(timeVal)),
                }));
              }}
              required
            />
          </Field>

          <Field>
            <label>Duration (mins):</label>
            <input
              name="duration"
              type="number"
              value={formData.duration}
              onChange={handleChange}
              required
            />
          </Field>

          {formData.type === 'lecture' && (
            <Field>
              <label>Slides Link:</label>
              <input
                name="slidesLink"
                value={formData.slidesLink}
                onChange={handleChange}
              />
            </Field>
          )}

          {formData.type === 'handout' && (
            <Field>
              <label>Handout Link:</label>
              <input
                name="handoutLink"
                value={formData.handoutLink}
                onChange={handleChange}
              />
            </Field>
          )}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
        >
          {editMode ? 'Save Changes' : 'Add Event'}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

const Field = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 0.75rem;

  label {
    font-weight: 500;
    margin-bottom: 0.25rem;
  }

  input,
  select {
    padding: 6px 8px;
    font-size: 0.95rem;
    border-radius: 4px;
    border: 1px solid #ccc;
  }
`;
